/**
 * BYO v2 — focused-modal header.
 *
 * A small sticky header (title + Save + Cancel) for the per-node BYO edit
 * modals. These modals render as the CONTENT of a fullscreen MUI Dialog owned by
 * ModalSourceForm / ModalDestinationForm, so they bring their own action bar.
 */

import React from 'react';
import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';

export interface ByoModalHeaderProps {
  title: string;
  subtitle?: string;
  saving?: boolean;
  /** Disable the Save button (e.g. while still hydrating). */
  saveDisabled?: boolean;
  /** Why Save is disabled — shown as the Save button's tooltip. */
  saveDisabledReason?: string | null;
  onSave: () => void;
  onCancel: () => void;
  /**
   * Read-only viewer mode (BYO pending-issues #7: "view code without
   * entering edit mode") — hides Save entirely and relabels the remaining
   * button "Close" instead of "Cancel", since there's nothing to discard.
   */
  readOnly?: boolean;
  'data-testid'?: string;
}

export function ByoModalHeader({
  title,
  subtitle,
  saving = false,
  saveDisabled = false,
  saveDisabledReason = null,
  onSave,
  onCancel,
  readOnly = false,
  'data-testid': dataTestId = 'byo-modal-header',
}: ByoModalHeaderProps) {
  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 3,
        py: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        position: 'sticky',
        top: 0,
        zIndex: 1,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h6" noWrap>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      <Button
        variant="text"
        color="inherit"
        onClick={onCancel}
        // The in-flight save can't be aborted and would still land after a discard.
        disabled={saving}
        data-testid={`${dataTestId}-cancel`}
      >
        {readOnly ? 'Close' : 'Cancel'}
      </Button>
      {readOnly ? null : (
        <Tooltip title={saveDisabledReason ?? ''} disableHoverListener={!saveDisabledReason}>
          {/* span: a disabled Button fires no hover events for the Tooltip. */}
          <span>
            <Button
              variant="contained"
              color="primary"
              onClick={onSave}
              disabled={saving || saveDisabled}
              data-testid={`${dataTestId}-save`}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
            </Button>
          </span>
        </Tooltip>
      )}
    </Box>
  );
}

export default ByoModalHeader;
